import { useApolloClient } from '@apollo/client';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAvailableLookupTypes } from 'modules/contactLookup/hooks/useAvailableLookupTypes';
import { ProjectId } from 'modules/domains/project';
import { WorkflowId } from 'modules/workflowIndex/types';
import { PREVIEW_YES_NO_SPLIT_MODAL } from 'modules/workflows/constants';
import { NodeId, SearchCombo } from 'modules/workflows/types';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, GhostButton, IconButton } from 'ui/button';
import { Add16, Delete16 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { Modal, MODAL_SIZES, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Body2 } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { WorkflowType } from 'modules/workflows/enums';
import { closeModal } from '../../../ducks';
import { CheckType, ResultType } from './PreviewYesNoSplitModal.constants';
import { YES_NO_SPLIT_TEST_QUERY } from './PreviewYesNoSplitModal.gql';
import {
  CheckUsersHeader,
  CheckUsersOptionsWrapper,
  CheckUsersResultWrapper,
  CheckUsersRow,
  CheckUsersWrapper,
  IdTypeSelect,
  SplitTestErrorBanner,
} from './PreviewYesNoSplitModal.styles';
import { UserToCheck } from './PreviewYesNoSplitModal.types';
import { stubUser } from './PreviewYesNoSplitModal.utils';
import SplitResult from './SplitResult';

export const ERROR_BANNER_TEST_ID = 'split-test-error-banner';
export const CANCEL_BUTTON_TEST_ID = 'discard-changes-button';
export const CHECK_BUTTON_TEST_ID = 'check-button';
export const ADD_USER_BUTTON_TEST_ID = 'add-user-button';
export const DELETE_USER_BUTTON_TEST_ID = 'delete-user-button';
export const USER_TEST_ID = 'user';
export const MAX_USERS = 5;

export interface PreviewYesNoSplitModalProps {
  /** The id of the journey being viewed */
  journeyId: WorkflowId;
  /** The id of the node/tile being viewed */
  nodeId: NodeId;
  /** The id of the current project */
  projectId: ProjectId;
  /** Whether the workflow is in published or draft mode */
  workflowType: WorkflowType;
  /** The search query to use for the split test */
  searchQuery: SearchCombo;
}

const PreviewYesNoSplitModal = ({
  journeyId,
  nodeId,
  projectId,
  workflowType,
  searchQuery,
}: PreviewYesNoSplitModalProps) => {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const availableLookupTypes = useAvailableLookupTypes();
  const canChooseLookupType = availableLookupTypes.length > 1;
  const [users, setUsers] = useState<UserToCheck[]>([stubUser(availableLookupTypes[0])]);
  const [hasErrors, setHasErrors] = useState(false);

  /**
   * Updates user with the given data
   */
  const updateUser = (i: number, data: Partial<UserToCheck> = {}) => {
    setUsers(users.map((user, currI) => (currI === i ? { ...user, ...data } : user)));
  };

  /**
   * Runs the check to test which branch the users will flow through
   */
  const runCheck = () => {
    setUsers(users.map((userToCheck) => ({ ...userToCheck, loading: true, error: false })));
    setHasErrors(false);

    const queries = users.map((user) =>
      // `useLazyQuery` hook is not able to be executed multiple times with
      // different variables, so we have to use `client.query` directly.
      // See: https://github.com/apollographql/apollo-client/issues/9755
      client.query({
        query: YES_NO_SPLIT_TEST_QUERY,
        variables: {
          journeyId,
          tileId: parseInt(nodeId, 10),
          projectId,
          userKey: user.value || '',
          searchQuery,
          workflowType,
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      }),
    );

    Promise.all(queries)
      .then((dataArray) => {
        let resultContainsError = false;
        const updatedUsers = users.map((user, index) => {
          const data = dataArray[index];
          const result = data?.data?.yesNoSplitTest?.workflowProcessResult as ResultType;
          const error = !!(result === 'error' || data?.error);

          if (error) {
            resultContainsError = true;
          }

          return { ...user, result, error, loading: false };
        });
        setUsers(updatedUsers);
        setHasErrors(resultContainsError);
      })
      .catch(() => {
        setHasErrors(true);
        setUsers(users.map((user) => ({ ...user, loading: false })));
      });
  };

  /**
   * Closes the modal
   */
  const handleCloseModal = () => dispatch(closeModal(PREVIEW_YES_NO_SPLIT_MODAL));

  return (
    <Modal
      id={PREVIEW_YES_NO_SPLIT_MODAL}
      isModalOpen={true}
      onClose={handleCloseModal}
      size={MODAL_SIZES.MEDIUM}
    >
      <ModalHeader>Preview split results</ModalHeader>
      <ModalBody>
        {hasErrors && (
          <SplitTestErrorBanner data-test={ERROR_BANNER_TEST_ID}>
            Hmm... something&apos;s not right. Please check your tile settings and the user data you
            entered and try again.
          </SplitTestErrorBanner>
        )}
        <Body2>Check if the following users flow through the Yes or No branch.</Body2>
        <CheckUsersWrapper>
          <CheckUsersRow>
            <CheckUsersOptionsWrapper>
              <CheckUsersHeader>Users to check</CheckUsersHeader>
            </CheckUsersOptionsWrapper>
            <CheckUsersResultWrapper>
              <CheckUsersHeader>Split result</CheckUsersHeader>
            </CheckUsersResultWrapper>
          </CheckUsersRow>
          {users.map(({ checkType, value, result, loading, error, id }, i) => (
            <CheckUsersRow key={id} data-test={USER_TEST_ID}>
              <CheckUsersOptionsWrapper>
                {canChooseLookupType && (
                  <IdTypeSelect
                    defaultValues={[checkType]}
                    options={[
                      { label: 'Email', value: CheckType.Email, id: CheckType.Email },
                      { label: 'User ID', value: CheckType.UserId, id: CheckType.UserId },
                    ]}
                    onSelect={([newValue]) => updateUser(i, { checkType: newValue as CheckType })}
                  />
                )}
                <InputText
                  hasError={error}
                  onChange={(newValue) => updateUser(i, { value: newValue })}
                  placeholder={`Add ${checkType === CheckType.Email ? 'user email' : 'user ID'}`}
                  value={value || ''}
                  width={canChooseLookupType ? pxToRem(300) : '100%'}
                />
                {users.length > 1 && (
                  <IconButton
                    data-test={DELETE_USER_BUTTON_TEST_ID}
                    variant={BUTTON_VARIANTS.GHOST}
                    onClick={() => setUsers(users.filter((_, uIndex) => uIndex !== i))}
                  >
                    <Delete16 />
                  </IconButton>
                )}
              </CheckUsersOptionsWrapper>
              <CheckUsersResultWrapper>
                <SplitResult result={result} loading={loading} />
              </CheckUsersResultWrapper>
            </CheckUsersRow>
          ))}
          {users.length < MAX_USERS && (
            <GhostButton
              onClick={() => setUsers([...users, stubUser(availableLookupTypes[0])])}
              data-test={ADD_USER_BUTTON_TEST_ID}
            >
              <Add16 /> Add another user
            </GhostButton>
          )}
        </CheckUsersWrapper>
      </ModalBody>
      <ModalFooter hasCancel={false}>
        <Button
          data-test={CANCEL_BUTTON_TEST_ID}
          onClick={handleCloseModal}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.GHOST}
        >
          Cancel
        </Button>
        <Button
          data-test={CHECK_BUTTON_TEST_ID}
          onClick={runCheck}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          Check
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PreviewYesNoSplitModal;
